import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	FormControl,
	HStack,
	Image,
	Input,
	Select,
	SimpleGrid,
	Text,
	Textarea,
	VStack,
	IconButton,
} from "@chakra-ui/react";
import { Plus, X } from "lucide-react";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import {
	FieldGrid,
	Label,
	commonInputProps,
	commonSelectProps,
	maybeErrorText,
} from "../matrimonyForm.ui";
import { MARITAL_STATUS_OPTIONS, RELIGION_OPTIONS } from "src/utils/matrimonyOptions";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";

type Props = {
	formData: MatrimonyFormData;
	errors: FormErrors;
	gender: string;
	setGender: (value: string) => void;
	updateField: (field: keyof MatrimonyFormData, value: string) => void;
	renderSegmentedButton: (
		optionValue: string,
		selectedValue: string,
		setSelectedValue: (value: string) => void,
		label: string
	) => React.ReactNode;
};

const MAX_ADDITIONAL = 4;

const Step1ProfileDetails: React.FC<Props> = ({
	formData,
	errors,
	gender,
	setGender,
	updateField,
	renderSegmentedButton,
}) => {
	const photoFile = formData.photoFile ?? null;
	const MAX_PHOTO_BYTES = 1 * 1024 * 1024; // 1MB
	const [localPhotoError, setLocalPhotoError] = useState<string>("");

	const additionalPhotos: (File | null)[] = useMemo(() => {
		const raw = formData.additionalPhotos ?? [];
		// Always pad to MAX_ADDITIONAL slots
		const padded = [...raw];
		while (padded.length < MAX_ADDITIONAL) padded.push(null);
		return padded.slice(0, MAX_ADDITIONAL);
	}, [formData.additionalPhotos]);

	const additionalPreviewUrls = useMemo(() => {
		return additionalPhotos.map((f) => (f ? URL.createObjectURL(f) : ""));
	}, [additionalPhotos]);

	useEffect(() => {
		return () => {
			additionalPreviewUrls.forEach((u) => { if (u) URL.revokeObjectURL(u); });
		};
	}, [additionalPreviewUrls]);

	const previewUrl = useMemo(() => {
		if (!photoFile) return "";
		return URL.createObjectURL(photoFile);
	}, [photoFile]);

	useEffect(() => {
		return () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		};
	}, [previewUrl]);

	// Clear local error once a valid file is present.
	useEffect(() => {
		if (photoFile) setLocalPhotoError("");
	}, [photoFile]);

	const filledCount = additionalPhotos.filter(Boolean).length;

	const handleAdditionalPhoto = (index: number, file: File | null) => {
		if (file && file.size > MAX_PHOTO_BYTES) {
			setLocalPhotoError("Each photo must be 1 MB or less");
			return;
		}
		setLocalPhotoError("");
		const next = [...additionalPhotos];
		next[index] = file;
		updateField("additionalPhotos", next as any);
	};

	const removeAdditionalPhoto = (index: number) => {
		const next = [...additionalPhotos];
		next[index] = null;
		// Compact: shift non-null items left, pad with null
		const compacted = next.filter(Boolean);
		while (compacted.length < MAX_ADDITIONAL) compacted.push(null);
		updateField("additionalPhotos", compacted as any);
	};

	return (
		<VStack align="stretch" spacing={4}>
			<Text fontSize="xs" fontWeight="600" color="#6C757D">
				{FIELD_LABELS.ADD_A_PHOTO.toUpperCase()}<span style={{ color: "#E53E3E" }}> *</span>
			</Text>
			<Box
				w="88px"
				h="88px"
				borderRadius="999px"
				bg="#F8F9FA"
				border="1px dashed rgba(108,117,125,0.5)"
				display="flex"
				alignItems="center"
				justifyContent="center"
				mb={2}
				mx={{ base: "auto", md: 0 }}
				as="label"
				cursor="pointer"
				role="group"
				overflow="hidden"
			>
				<Input
					type="file"
					accept="image/jpeg,image/jpg,image/png"
					display="none"
					onChange={(e) => {
						const file = e.target.files?.[0] || null;
						if (file && file.size > MAX_PHOTO_BYTES) {
							setLocalPhotoError("Photo must be 1MB or less");
							// Clear selection so user can pick again.
							e.currentTarget.value = "";
							updateField("photoFile", "" as any);
							return;
						}
						setLocalPhotoError("");
						updateField("photoFile", file as any);
					}}
				/>

				{previewUrl ? (
					<Image
						src={previewUrl}
						alt="Selected profile photo"
						w="100%"
						h="100%"
						objectFit="cover"
					/>
				) : (
					<Text
						fontSize="xs"
						color="#6C757D"
						_groupHover={{ color: "#4A2C2A" }}
					>
						Upload
					</Text>
				)}
			</Box>

			{/* ── Additional Photos (max 4) ────────────────────────── */}
			<HStack justify="space-between" align="center" mt={1}>
				<Text fontSize="xs" fontWeight="600" color="#6C757D" textTransform="uppercase">
					Additional Photos (Max {MAX_ADDITIONAL})
				</Text>
				<Text fontSize="xs" fontWeight="600" color="#4A2C2A">
					{filledCount}/{MAX_ADDITIONAL} ADDED
				</Text>
			</HStack>

			<SimpleGrid columns={4} spacing={3}>
				{additionalPhotos.map((file, idx) => (
					<Box
						key={idx}
						position="relative"
						w="full"
						h="0"
						pb="100%"   /* square aspect ratio */
						borderRadius="12px"
						border="1px dashed rgba(108,117,125,0.4)"
						bg="#F8F9FA"
						overflow="hidden"
					>
						{file && additionalPreviewUrls[idx] ? (
							<>
								<Image
									src={additionalPreviewUrls[idx]}
									alt={`Additional photo ${idx + 1}`}
									position="absolute"
									top={0}
									left={0}
									w="100%"
									h="100%"
									objectFit="cover"
								/>
								<IconButton
									aria-label="Remove photo"
									icon={<X size={14} />}
									size="xs"
									position="absolute"
									top={1}
									right={1}
									borderRadius="full"
									bg="rgba(0,0,0,0.55)"
									color="white"
									_hover={{ bg: "rgba(0,0,0,0.75)" }}
									onClick={() => removeAdditionalPhoto(idx)}
								/>
							</>
						) : (
							<Box
								as="label"
								cursor="pointer"
								position="absolute"
								top={0}
								left={0}
								w="100%"
								h="100%"
								display="flex"
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								_hover={{ bg: "rgba(74,44,42,0.04)" }}
							>
								<Input
									type="file"
									accept="image/jpeg,image/jpg,image/png"
									display="none"
									onChange={(e) => {
										handleAdditionalPhoto(idx, e.target.files?.[0] || null);
										e.currentTarget.value = "";
									}}
								/>
								<Plus size={20} color="#6C757D" />
								<Text fontSize="2xs" color="#6C757D" mt={1}>
									ADD PHOTO {idx + 1}
								</Text>
							</Box>
						)}
					</Box>
				))}
			</SimpleGrid>

			{maybeErrorText(localPhotoError || errors.photoFile)}

			<FieldGrid>
				<FormControl flex="1" isRequired isInvalid={Boolean(errors.fullName)}>
					<Label>{FIELD_LABELS.FULL_NAME.toUpperCase()}</Label>
					<Input
						{...commonInputProps}
						autoComplete="name"
						value={formData.fullName}
						onChange={(event) => updateField("fullName", event.target.value)}
						placeholder="Enter the full name"
					/>
					{maybeErrorText(errors.fullName)}
				</FormControl>

				<FormControl flex="1" isRequired isInvalid={Boolean(errors.dob)}>
					<Label>{FIELD_LABELS.DATE_OF_BIRTH.toUpperCase()}</Label>
					<Input
						{...commonInputProps}
						autoComplete="bday"
						type="date"
						max={new Date().toISOString().split("T")[0]}
						value={formData.dob}
						onChange={(event) => updateField("dob", event.target.value)}
					/>
					{maybeErrorText(errors.dob)}
				</FormControl>
			</FieldGrid>

			<FieldGrid>
				<FormControl flex="1" isRequired isInvalid={Boolean(errors.emailId)}>
					<Label>{FIELD_LABELS.EMAIL_ID.toUpperCase()}</Label>
					<Input
						{...commonInputProps}
						autoComplete="email"
						type="email"
						value={formData.emailId}
						onChange={(event) => updateField("emailId", event.target.value)}
						placeholder="Enter email ID"
						isDisabled
					/>
					{maybeErrorText(errors.emailId)}
				</FormControl>

				<FormControl flex="1" isRequired isInvalid={Boolean(errors.mobileNumber)}>
					<Label>{FIELD_LABELS.MOBILE_NUMBER.toUpperCase()}</Label>
					<Input
						{...commonInputProps}
						autoComplete="tel"
						type="tel"
						value={formData.mobileNumber}
						onChange={(event) => updateField("mobileNumber", event.target.value)}
						placeholder="Enter mobile number"
						isDisabled
					/>
					{maybeErrorText(errors.mobileNumber)}
				</FormControl>
			</FieldGrid>

			<FieldGrid>
				<FormControl flex="1" isRequired>
					<Label>{FIELD_LABELS.GENDER.toUpperCase()}</Label>
					<HStack spacing={3}>
						{renderSegmentedButton("Male", gender, setGender, "Male")}
						{renderSegmentedButton("Female", gender, setGender, "Female")}
					</HStack>
				</FormControl>

				<FormControl
					flex="1"
					isRequired
					isInvalid={Boolean(errors.maritalStatus)}
				>
					<Label>{FIELD_LABELS.MARITAL_STATUS.toUpperCase()}</Label>
					<Select
						{...commonSelectProps}
						value={formData.maritalStatus}
						onChange={(event) =>
							updateField("maritalStatus", event.target.value)
						}
						placeholder="Select your marital status"
					>
						{MARITAL_STATUS_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</Select>
					{maybeErrorText(errors.maritalStatus)}
				</FormControl>
			</FieldGrid>

			<FieldGrid>
				<FormControl flex="1">
					<Label>{FIELD_LABELS.TIME_OF_BIRTH.toUpperCase()}</Label>
					<Input
						{...commonInputProps}
						type="time"
						value={formData.timeOfBirth || ""}
						onChange={(event) => updateField("timeOfBirth", event.target.value)}
						placeholder="HH:MM"
					/>
				</FormControl>

				<FormControl flex="1" isRequired>
					<Label>{FIELD_LABELS.RELIGION.toUpperCase()}</Label>
					<Select
						{...commonSelectProps}
						value={formData.religion || "HINDU"}
						onChange={(event) => updateField("religion", event.target.value)}
						placeholder="Select religion"
					>
						{RELIGION_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</Select>
				</FormControl>
			</FieldGrid>

			<FormControl>
				<Label>{FIELD_LABELS.ABOUT.toUpperCase()}</Label>
				<Textarea
					{...commonInputProps}
					value={formData.about || ""}
					onChange={(event) => updateField("about", event.target.value)}
					placeholder="Write something about yourself"
					rows={4}
				/>
			</FormControl>

			<FormControl>
				<Label>{FIELD_LABELS.CASTE_DETAILS.toUpperCase()}</Label>
				<Input
					{...commonInputProps}
					value={formData.casteDetails}
					onChange={(event) => updateField("casteDetails", event.target.value)}
					placeholder="Enter caste details"
				/>
			</FormControl>
		</VStack>
	);
};

export default Step1ProfileDetails;
